import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Eye, LogOut, PenLine, ShieldAlert, X } from "lucide-react";
import { api, apiError, SUPPORT_TOKEN_KEY, registerWriteGuard } from "@/lib/api";

const SupportContext = createContext(null);
export const useSupport = () => useContext(SupportContext);

const minutesLeft = (iso) =>
  Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60000));

export function SupportProvider({ children }) {
  const [ctx, setCtx] = useState(null);
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    if (!localStorage.getItem(SUPPORT_TOKEN_KEY)) {
      setCtx(null);
      return;
    }
    try {
      const { data } = await api.get("/support/context");
      setCtx(data.active ? data : null);
      if (!data.active) localStorage.removeItem(SUPPORT_TOKEN_KEY);
    } catch {
      localStorage.removeItem(SUPPORT_TOKEN_KEY);
      setCtx(null);
    }
  }, []);

  useEffect(() => {
    load();
    const onStorage = () => load();
    window.addEventListener("ciftora-support-changed", onStorage);
    const t = setInterval(load, 60000);
    return () => {
      window.removeEventListener("ciftora-support-changed", onStorage);
      clearInterval(t);
    };
  }, [load]);

  const exit = useCallback(
    async (message = "Destek oturumu kapatıldı") => {
      let closed = true;
      try {
        await api.post("/support/end");
      } catch (e) {
        closed = false;
        // yedek: admin tokenıyla oturumu kapat (destek tokenı reddedilmiş olabilir)
        try {
          const sid = ctx?.session_id;
          if (sid) await api.post(`/admin/support/sessions/${sid}/end`);
          closed = true;
        } catch (err) {
          toast.error(`Oturum sunucuda kapatılamadı: ${apiError(err || e)}`);
        }
      }
      localStorage.removeItem(SUPPORT_TOKEN_KEY);
      setCtx(null);
      if (closed) toast.success(message);
      // tam sayfa dönüş: admin oturumu ve önbellekler temiz şekilde geri yüklenir
      window.location.assign("/yonetim/destek-erisimleri");
    },
    [ctx],
  );

  // Yönetim modunda her yazma isteği öncesi onay + sebep (yanlışlıkla işlem riskini azaltır)
  useEffect(() => {
    registerWriteGuard(async (config) => {
      if (!localStorage.getItem(SUPPORT_TOKEN_KEY)) return true;
      return new Promise((resolve) => {
        setPending({ config, resolve });
        setReason("");
      });
    });
    return () => registerWriteGuard(null);
  }, []);

  const value = useMemo(() => ({ support: ctx, reload: load, exit }), [ctx, load, exit]);

  return (
    <SupportContext.Provider value={value}>
      {ctx && <SupportBanner ctx={ctx} onExit={exit} />}
      {children}
      {pending && (
        <div
          data-testid="support-write-confirm"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
        >
          <div className="w-full max-w-md rounded-2xl bg-background p-6">
            <h2 className="flex items-center gap-2 text-base font-bold text-primary md:text-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> Yönetim Destek Modunda işlem
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Bu işlemi <b>{ctx?.target?.name || ctx?.target?.email}</b> hesabında
              gerçekleştiriyorsunuz. Kayıt, sizin adınıza ve destek oturumu numarasıyla saklanır.
            </p>
            <p className="mt-2 break-all rounded-lg bg-muted p-2 text-xs text-muted-foreground">
              {pending.config?.method?.toUpperCase()} {pending.config?.url}
            </p>
            <label className="mt-3 block text-xs font-semibold text-muted-foreground">
              İşlem sebebi (zorunlu)
              <input
                data-testid="support-write-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 h-11 w-full rounded-lg border border-border bg-background px-3 text-sm font-normal text-foreground"
                placeholder="Örn. Destek talebi #1234 kapsamında stok düzeltmesi"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                data-testid="support-write-cancel"
                onClick={() => {
                  pending.resolve(false);
                  setPending(null);
                }}
                className="inline-flex h-11 items-center rounded-full border border-border px-4 text-sm font-semibold"
              >
                Vazgeç
              </button>
              <button
                data-testid="support-write-approve"
                disabled={!reason.trim()}
                onClick={() => {
                  pending.resolve(reason.trim());
                  setPending(null);
                }}
                className="inline-flex h-11 items-center rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                İşlemi Onayla
              </button>
            </div>
          </div>
        </div>
      )}
    </SupportContext.Provider>
  );
}

function SupportBanner({ ctx, onExit }) {
  const manage = ctx.mode === "MANAGE";
  return (
    <div
      data-testid="support-banner"
      className={`sticky top-0 z-50 w-full border-b-2 px-4 py-2.5 ${
        manage ? "border-red-700 bg-red-600 text-white" : "border-amber-600 bg-amber-500 text-black"
      }`}
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-2">
        <span className="flex items-center gap-2 text-sm font-extrabold uppercase">
          <ShieldAlert className="h-5 w-5" /> Yönetim Destek Modu
        </span>
        <span data-testid="support-banner-target" className="text-sm font-semibold">
          {ctx.target?.name || ctx.target?.email} hesabını görüntülüyorsunuz
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-black/15 px-2.5 py-1 text-xs font-bold">
          {manage ? <PenLine className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {ctx.mode_label} · {ctx.view_scope_label}
        </span>
        <span data-testid="support-banner-timer" className="text-xs font-semibold">
          Kalan süre: {minutesLeft(ctx.expires_at)} dk
        </span>
        <div className="ml-auto flex gap-2">
          <a
            data-testid="support-banner-admin-link"
            href="/yonetim/destek-erisimleri"
            className="inline-flex h-9 items-center gap-1 rounded-full bg-black/15 px-3 text-xs font-bold"
          >
            <LogOut className="h-3.5 w-3.5" /> Admin Paneline Dön
          </a>
          <button
            data-testid="support-banner-end"
            onClick={() => onExit("Destek oturumu bitirildi")}
            className="inline-flex h-9 items-center gap-1 rounded-full bg-black px-3 text-xs font-bold text-white"
          >
            <X className="h-3.5 w-3.5" /> Destek Oturumunu Bitir
          </button>
        </div>
      </div>
    </div>
  );
}
